import { useContext } from 'react';
import { AuthContext } from '../../features/login/AuthWrapper';
import { isLegacySystemRole } from '../constants/roles';
import { FeatureFlagsContext } from '../featureFlagsWrapper';

interface UseAccessOptions {
  permission: string;
  /**
   * Optional legacy role-based check.
   * [TRANSITION] Omit for new features that have no legacy equivalent.
   * When omitted, the legacy side defaults to allowed (true).
   */
  legacyCheck?: (roles: string[]) => boolean;
}

/**
 * [TRANSITION] Access check hook mirroring the backend dual-guard architecture.
 *
 * Role prefixes are the discriminator between systems:
 *   Old system → UW_* roles or v1 carrier roles (isLegacySystemRole)
 *   v2 system  → WORKBENCH_* prefixed roles
 *
 * When FF is off:
 *   → legacyCheck runs for all users if provided; else true.
 *   → v2 permission not evaluated.
 *
 * When FF is on — AND logic, each side only evaluates if the user has the relevant roles:
 *   legacyAllowed = has old roles ? legacyCheck(roles) : true
 *   v2Allowed     = has WORKBENCH_ roles ? permissions.has(permission) : true
 *   result        = legacyAllowed && v2Allowed
 *
 * Migration phases:
 *   Phase A (old roles only)  → legacyCheck enforces, v2 skips (true)
 *   Phase B (both role types) → both evaluate; AND applies
 *   Phase C (new roles only)  → legacyCheck skips (true), v2 enforces
 *
 * Omit legacyCheck when v1 had no role gate — returns true when FF is off.
 */
export function useAccess({
  permission,
  legacyCheck,
}: UseAccessOptions): boolean {
  const { roles, permissions } = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const isNewRbacEnabled = flags?.isNewRbacEnabled === true;

  // [TRANSITION] Old-system roles use UW_* prefixes or v1 carrier roles — mirrors PermissionsGuard.
  const hasOldRoles = roles.some(isLegacySystemRole);

  // [TRANSITION] v2 roles use WORKBENCH_ prefix — mirrors PermissionGuardV2.
  const hasNewRoles = roles.some(r => r.startsWith('WORKBENCH_'));

  // [TRANSITION] When FF is off, only run the legacy check — v2 is not enforced yet.
  if (!isNewRbacEnabled) {
    return legacyCheck ? legacyCheck(roles) : true;
  }

  // [TRANSITION] FF is on — AND logic, each side evaluates only for its role domain.
  // legacyCheck skips (true) when user has no old roles — mirrors PermissionsGuard skipping.
  // v2 check skips (true) when user has no WORKBENCH_ roles — mirrors PermissionGuardV2 skipping.
  const legacyAllowed = hasOldRoles && legacyCheck ? legacyCheck(roles) : true;

  if (hasNewRoles && !permissions) {
    // Permissions not yet populated — fail closed until confirmed.
    return false;
  }

  const v2Allowed = hasNewRoles ? permissions?.has(permission) ?? true : true;

  return legacyAllowed && v2Allowed;
}
